import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'antd';
import { deauthenticate } from '../store/api/apiSlice';
import { useRenewSessionMutation } from '../store/api/authApi';
import { selectAuth } from '../store/slices/authSlice';
import { t } from '../i18n/i18n';
import './SessionExpiryDialog.scss';

/** How long before the token dies the trader is asked — the countdown's length. */
const WARN_MS = 20 * 1000;

/**
 * How long without a click, key, scroll or pointer move counts as away — N117.
 * Someone active within this window when the token nears its end is renewed
 * silently; only someone idle past it is asked "Still there?".
 */
const IDLE_MS = 10 * 60 * 1000;

/** What counts as using the dashboard. */
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'];

const RING_R = 44;
const RING_C = 2 * Math.PI * RING_R;

const formatRemaining = (ms) => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = String(total % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
};

/**
 * "Stay signed in or sign out?" — for a session opened WITHOUT "Remember for
 * 30 days". Such a session is meant to end with its hour-long token (see
 * `selectIsSessionValid`), and the 401 handler won't renew it on its own; this
 * asks shortly before that happens instead of dropping the trader on the
 * sign-in form mid-task. A remembered session renews silently and never sees it.
 *
 * Mounted once in the shell. Letting the countdown run out signs out exactly as
 * the Log out button does, so a tab left alone doesn't keep a dead session.
 *
 * It is a question for someone who has stepped away, not for someone working
 * (N117): the token's hour runs out on a timer, not on idleness, so a trader
 * still clicking through the dashboard is renewed in the background when the
 * window opens, and only asked if they've been idle for IDLE_MS or the silent
 * renewal fails.
 */
export default function SessionExpiryDialog() {
  const dispatch = useDispatch();
  const { token, expiresAt, remember, refreshToken } = useSelector(selectAuth);
  const [renewSession, { isLoading }] = useRenewSessionMutation();
  const [now, setNow] = useState(() => Date.now());
  const [error, setError] = useState(null);
  /** Whether this token's warning window was judged "away" (or the silent renewal failed). */
  const [prompting, setPrompting] = useState(false);

  // The last moment the trader did anything. A ref, not state: it changes on
  // every pointer move and nothing renders from it until the window opens.
  const lastActive = useRef(Date.now());
  useEffect(() => {
    const mark = () => {
      lastActive.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach((type) =>
      window.addEventListener(type, mark, { passive: true, capture: true }),
    );
    return () =>
      ACTIVITY_EVENTS.forEach((type) =>
        window.removeEventListener(type, mark, { capture: true }),
      );
  }, []);

  const watching = Boolean(token) && !remember && expiresAt != null;
  const remaining = watching ? expiresAt - now : Infinity;
  const inWindow = remaining <= WARN_MS;
  const open = inWindow && prompting;

  // A new token (renewed either way) starts its hour with no question pending.
  useEffect(() => {
    setPrompting(false);
    setError(null);
  }, [expiresAt]);

  // The window opens: renew quietly for someone who's here, ask someone who
  // isn't. A failed quiet renewal falls back to asking, with the reason.
  // Decided once per token — `decidedFor` holds the expiry it was decided for,
  // so the per-second ticks through the window don't decide it again.
  const decidedFor = useRef(null);
  useEffect(() => {
    if (!watching || !inWindow || Date.now() >= expiresAt) return undefined;
    if (decidedFor.current === expiresAt) return undefined;
    decidedFor.current = expiresAt;
    const away = Date.now() - lastActive.current >= IDLE_MS;
    if (away || !refreshToken) {
      setPrompting(true);
      return undefined;
    }
    let cancelled = false;
    renewSession()
      .unwrap()
      .catch(() => {
        if (cancelled) return;
        setError(t('We could not extend your session. Please sign in again.'));
        setPrompting(true);
      });
    return () => {
      cancelled = true;
    };
  }, [watching, inWindow, expiresAt, refreshToken, renewSession]);

  const seconds = Math.max(0, Math.ceil(remaining / 1000));
  const progress = open ? Math.min(1, Math.max(0, remaining / WARN_MS)) : 1;
  // The ring and number warm up as the time runs out.
  const stage = seconds <= 5 ? 'urgent' : seconds <= 10 ? 'warn' : 'calm';

  // Sleep until the warning window, then tick every second through it. A
  // renewed token moves `expiresAt`, which re-arms the wait. `inWindow` is a
  // dep so the timeout firing re-runs this and starts the interval — without
  // it the effect stays on the spent timeout and the countdown freezes. (Not
  // `open`: during a silent renewal the window is open but the dialog isn't,
  // and the clock still has to run in case the renewal never answers.)
  useEffect(() => {
    if (!watching) return undefined;
    const untilWarn = expiresAt - WARN_MS - Date.now();
    if (untilWarn > 0) {
      const timeout = setTimeout(() => setNow(Date.now()), untilWarn);
      return () => clearTimeout(timeout);
    }
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [watching, expiresAt, inWindow]);

  // Also covers a laptop waking up long past the expiry.
  useEffect(() => {
    if (watching && remaining <= 0) dispatch(deauthenticate());
  }, [watching, remaining, dispatch]);

  const signOut = () => {
    setError(null);
    dispatch(deauthenticate());
  };

  const staySignedIn = async () => {
    setError(null);
    try {
      await renewSession().unwrap();
    } catch {
      setError(t('We could not extend your session. Please sign in again.'));
    }
  };

  return (
    <Modal
      open={open && remaining > 0}
      // No close button, Escape or mask click: dismissing it without an
      // answer would only let the session lapse unseen.
      footer={null}
      closable={false}
      mask={{ closable: false }}
      keyboard={false}
      centered
      width={392}
      destroyOnHidden
      className="session-dialog"
      rootClassName="session-dialog-root"
      aria-label={t('Session expiring')}
    >
      <div className={`session-dialog__body session-dialog__body--${stage}`}>
        <div
          className="session-dialog__timer"
          role="timer"
          aria-live="off"
          aria-label={t('{{seconds}} seconds until sign-out', { seconds })}
        >
          <svg className="session-dialog__ring" viewBox="0 0 100 100" aria-hidden="true">
            <circle className="session-dialog__ring-track" cx="50" cy="50" r={RING_R} />
            <circle
              className="session-dialog__ring-fill"
              cx="50"
              cy="50"
              r={RING_R}
              strokeDasharray={RING_C}
              strokeDashoffset={RING_C * (1 - progress)}
            />
          </svg>
          <span className="session-dialog__time">
            <span className="session-dialog__time-value">
              {seconds >= 60 ? formatRemaining(remaining) : seconds}
            </span>
            <span className="session-dialog__time-unit">{seconds >= 60 ? t('min') : t('sec')}</span>
          </span>
        </div>

        <div className="session-dialog__head">
          <h2 className="session-dialog__title">{t('Still there?')}</h2>
          <p className="session-dialog__desc">
            {t(
              "Your session is about to expire. For your security you'll be signed out automatically unless you choose to stay.",
            )}
          </p>
        </div>

        {error && (
          <p className="session-dialog__error" role="alert">
            {error}
          </p>
        )}

        <div className="session-dialog__actions">
          <button type="button" className="session-dialog__logout" onClick={signOut} disabled={isLoading}>
            {t('Log out')}
          </button>
          <button
            type="button"
            className="session-dialog__stay"
            onClick={staySignedIn}
            disabled={isLoading || !refreshToken || Boolean(error)}
          >
            {isLoading ? t('Extending…') : t('Stay signed in')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
